export type TransportMode = "train" | "car" | "carpool" | "bus" | "plane";

export interface CarbonInputs {
  participants: number;
  distance: number;
  days: number;
  mealsPerDay: number;
  transportMode: TransportMode;
  vegetarianPercentage: number;
  withAccommodation: boolean;
}

export interface CarbonBreakdown {
  transport: number;
  meals: number;
  accommodation: number;
  total: number;
  perPerson: number;
}

export const transportModeLabels: Record<TransportMode, string> = {
  train: "Train",
  car: "Voiture",
  carpool: "Covoiturage",
  bus: "Bus",
  plane: "Avion"
};

const TRANSPORT_EMISSIONS: Record<TransportMode, number> = {
  train: 0.0029,
  car: 0.218,
  carpool: 0.0545,
  bus: 0.113,
  plane: 0.259,
};

const MEAL_EMISSIONS = {
  standard: 2.5,
  vegetarian: 0.45,
};

const ACCOMMODATION_EMISSION = 6.7;

export function calculateCarbonFootprint(inputs: CarbonInputs): CarbonBreakdown {
  const { participants, distance, days, mealsPerDay, transportMode, vegetarianPercentage, withAccommodation } = inputs;

  const roundTripDistance = distance * 2;
  const transportEmission = roundTripDistance * participants * TRANSPORT_EMISSIONS[transportMode];

  const totalMeals = days * mealsPerDay * participants;
  const vegetarianRatio = vegetarianPercentage / 100;
  const standardRatio = 1 - vegetarianRatio;
  const mealsEmission = totalMeals * (standardRatio * MEAL_EMISSIONS.standard + vegetarianRatio * MEAL_EMISSIONS.vegetarian);

  const nights = Math.max(0, days - 1);
  const accommodationEmission = withAccommodation ? nights * participants * ACCOMMODATION_EMISSION : 0;

  const total = transportEmission + mealsEmission + accommodationEmission;
  const perPerson = participants > 0 ? Math.round(total / participants) : 0;

  return {
    transport: Math.round(transportEmission),
    meals: Math.round(mealsEmission),
    accommodation: Math.round(accommodationEmission),
    total: Math.round(total),
    perPerson,
  };
}
